import { Link } from 'react-router-dom'
import styles from '../pages/layout/Layout.module.css'
import { Button } from './ui/button'
import { motion, AnimatePresence } from 'framer-motion'

import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent
} from '@/components/ui/sidebar'
import { ChevronDown, MessageCircle, LayoutGrid } from 'lucide-react'
import { useContext, useState } from 'react'
import ChatHistoryList from '@/components/ChatHistory/ChatHistoryList'
import { AppStateContext } from '@/state/AppProvider'

type SidebarProps = {
  header: {
    logoSrc: string
    alt: string
    label: string
    link: string
  }
  content: {
    text: string
    buttonText: string
    dropdownItems?: { link: string; text: string; icon?: React.ElementType }[]
  }
  dynamicContent?: {
    chatHistory?: boolean
  }
  admin?: boolean // <-- new prop
  onAdminClick?: () => void // <-- handler for admin button
}

export function AppSidebar({ header, content, dynamicContent, admin, onAdminClick }: SidebarProps) {
  const [open, setOpen] = useState(false)
  const appStateContext = useContext(AppStateContext)

  return (
    <Sidebar className="border-r-slate-300 flex flex-col h-full">
      <SidebarHeader className="bg-slate-100">
        <Link to={header.link} className="flex flex-row items-center justify-center gap-3 py-4">
          <img src={header.logoSrc} alt={header.alt} className="h-12 w-12 object-contain" />
          <span className={`${styles.headerTitle} font-bold text-[1.8rem]`}>{header.label}</span>
        </Link>
      </SidebarHeader>

      <SidebarContent className="scrollbar-hide bg-slate-100 flex-1 flex flex-col justify-between">
        <div>
          <div className="flex flex-row items-center justify-center gap-3 py-2">
            <Button
              onClick={appStateContext?.state.layoutButtonAction}
              className="rounded-full bg-white text-gray-600 hover:bg-gray-100 px-4 min-w-[190px]"
            >
              <span className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                <span className="pr-3">{content.text}</span>
              </span>
              {/* <span className="flex items-center gap-2">
                <span className="h-4 w-px bg-gray-300" />
                <ChevronDown className="h-4 w-4" />
              </span> */}
            </Button>
          </div>

          <div className="relative items-center flex flex-col gap-3 py-2">
            <Button
              asChild
              onClick={() => setOpen(!open)}
              className="rounded-full bg-white text-gray-600 hover:bg-gray-100 px-4 flex items-center gap-2 w-[190px]"
            >
              <span className="flex items-center gap-2 cursor-pointer">
                <LayoutGrid className="h-5 w-5" />
                <span>{content.buttonText}</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
              </span>
            </Button>

            <AnimatePresence>
              {open && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="mt-2 w-[190px] rounded-xl overflow-hidden shadow-sm border border-gray-200 bg-white"
                >
                  {content.dropdownItems?.map((item, i) => (
                    <Link
                      key={i}
                      to={item.link}
                      className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-2 px-4 py-2 text-gray-600 text-sm hover:bg-gray-50 border-b last:border-b-0"
                    >
                      {item.icon && <item.icon className="h-5 w-5 text-gray-500" />}
                      <span>{item.text}</span>
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {dynamicContent?.chatHistory && (
            <SidebarGroup className="pl-8">
              <SidebarGroupLabel className="text-lg font-thin pb-2">Chat History</SidebarGroupLabel>
              <SidebarGroupContent className="p-0">
                <div className="w-[190px] pl-2">
                  <ChatHistoryList />
                </div>
              </SidebarGroupContent>
            </SidebarGroup>
          )}
        </div>

        {/* Footer for admin */}
        {admin && (
          <div className="flex justify-center py-4">
            <Button
              onClick={onAdminClick}
              className="rounded-full bg-blue-200 text-gray-500 hover:text-gray-600 hover:bg-blue-300 px-4 w-[190px]"
            >
              Admin Page
            </Button>
          </div>
        )}
      </SidebarContent>
    </Sidebar>
  )
}